import { useState } from 'react';
import { useDeal } from '../../../hooks/useDeal';
import { renderInlineMd, formatDate, formatTime } from '../../../lib/utils';
import { filesApi } from '../../../services/api';
import type { OutstandingResearch, InvestigationStage, WorkingTheory } from '../../../types';

interface Props { dealId: string; }


export function TitleTab({ dealId }: Props) {
  const { deal, data } = useDeal(dealId);

  const [showResearch, setShowResearch] = useState(true);
  const [showStages, setShowStages] = useState(false);
  const [showDeadEnds, setShowDeadEnds] = useState(false);
  const [showSupporting, setShowSupporting] = useState(false);

  if (!deal) return null;

  const openFlags = data.flags.filter(f => !f.resolved);
  const openResearch = data.outstandingResearch.filter(r => !r.resolved);
  const resolvedResearch = data.outstandingResearch.filter(r => r.resolved);
  const theory = data.workingTheory as (WorkingTheory | undefined);


  // ─── Layout ────────────────────────────────────────────
  return (
    <>
      <div className="workspace-head">
        <div>
          <h2 className="workspace-title">Title <em>workspace</em></h2>
          <div className="workspace-subtitle">
            {deal.ownerName} · <strong style={{ color: 'var(--accent)' }}>{deal.trs}</strong> · {deal.county}
            {deal.operator && <> · {deal.operator}</>}
          </div>
        </div>
        <div className="workspace-actions">
          <a
            className="btn-ghost"
            href={clerkUrl(deal.county, deal.trs)}
            target="_blank"
            rel="noreferrer"
            title="Open the county recorder"
          >⌕ Clerk ↗</a>
        </div>
      </div>


      {/* PRIMARY: Title summary thesis */}
      <ThesisPanel
        theory={theory}
        investigating={false}
        showSupporting={showSupporting}
        setShowSupporting={setShowSupporting}
      />

      {/* MATH WORKSHEET — receipts under the thesis */}
      {data.math && (
        <div className="math-worksheet">
          <div className="math-head">
            <div>
              <div className="math-title">NMA <em>worksheet</em></div>
              <div className="workspace-subtitle" style={{ marginTop: 4 }}>
                Derived from chain · cross-checked vs stub
              </div>
            </div>
            <div className="math-result">{data.math.result}</div>
          </div>
          <div className="math-rows">
            {data.math.rows.map((r, i) => (
              <div
                key={i}
                className={`math-row ${r.kind ?? ''}`}
                style={
                  r.kind === 'flag' ? { background: 'rgba(212, 161, 71, 0.06)', margin: '0 -12px', padding: '8px 12px', borderRadius: 3 } :
                  r.kind === 'cross-check' ? { background: 'rgba(127, 168, 126, 0.04)', margin: '0 -12px', padding: '8px 12px', borderRadius: 3, borderBottom: 'none' } :
                  undefined
                }
              >
                <span className="label" dangerouslySetInnerHTML={{ __html: renderInlineMd(r.label) }} />
                <span
                  className="value"
                  style={
                    r.kind === 'flag' ? { color: 'var(--amber)' } :
                    r.kind === 'cross-check' ? { color: 'var(--green)' } :
                    undefined
                  }
                >{r.value}</span>
                <span className="source">{r.source}</span>
              </div>
            ))}
          </div>
          {data.math.reconciliation && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--border)', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--accent)', fontFamily: 'var(--display)', fontStyle: 'italic' }}>
                Reconciliation:
              </strong>{' '}
              <span dangerouslySetInnerHTML={{ __html: renderInlineMd(data.math.reconciliation) }} />
            </div>
          )}
        </div>
      )}

      {/* CHAIN OF CONVEYANCE */}
      {data.chain.length > 0 && (
        <div className="chain-viz">
          <div className="chain-viz-head">
            <div className="chain-viz-title">Chain of <em>conveyance</em></div>
            <div className="chain-viz-meta">{deal.trs} · {deal.county}</div>
          </div>
          <div className="chain">
            {data.chain.map(link => {
              const onOpenFile = () => {
                if (link.fileId) window.open(filesApi.downloadUrl(link.fileId), '_blank');
              };
              return (
                <div
                  key={link.id}
                  className={`chain-link ${link.status} ${link.fileId ? 'clickable' : ''}`}
                  onDoubleClick={onOpenFile}
                  title={link.fileId ? 'Double-click to open source file' : undefined}
                >
                  <div className="chain-node">{link.ordinal}</div>
                  <div>
                    <div className="chain-doc">
                      {link.docType}
                      {link.docNumber && <> · <span className="doc-num">{link.docNumber}</span></>}
                      {' '}· {formatDate(link.date)}
                    </div>
                    <div
                      className="chain-conveyance"
                      dangerouslySetInnerHTML={{ __html: renderInlineMd(link.conveyance) }}
                    />
                    {link.detail && (
                      <div className="chain-detail">
                        {link.detail.split(/\s·\s/).map((d, i, a) => (
                          <span key={i}>{d}{i < a.length - 1 && ' · '}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="chain-actions">
                    {link.fileId && (
                      <button
                        className="chain-open-btn"
                        onClick={(e) => { e.stopPropagation(); onOpenFile(); }}
                        title="Open source file"
                      >Open ↗</button>
                    )}
                    <span className={`chain-status ${link.status}`}>
                      {link.status === 'verified' ? 'Verified' :
                       link.status === 'flagged'  ? 'Needs review' :
                       link.status === 'current'  ? 'Current' :
                       'Pending'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OUTSTANDING RESEARCH — collapsible. Open by default if any items. */}
      {(openResearch.length > 0 || data.investigationStages.length > 0 || resolvedResearch.length > 0 || data.deadEnds.length > 0) && (
        <div className="title-secondary">
          {openResearch.length > 0 && (
            <SecondaryBlock
              title={`Outstanding research`}
              count={openResearch.length}
              expanded={showResearch}
              onToggle={() => setShowResearch(!showResearch)}
            >
              <div className="research-list">
                {openResearch.map(r => (
                  <ResearchItem key={r.id} item={r} />
                ))}
              </div>
            </SecondaryBlock>
          )}

          {data.investigationStages.length > 0 && (
            <SecondaryBlock
              title="Investigation history"
              count={data.investigationStages.length}
              expanded={showStages}
              onToggle={() => setShowStages(!showStages)}
            >
              <div className="stage-list">
                {data.investigationStages.slice().reverse().map(s => <StageRow key={s.id} stage={s} />)}
              </div>
            </SecondaryBlock>
          )}

          {(resolvedResearch.length > 0 || data.deadEnds.length > 0) && (
            <SecondaryBlock
              title="Audit trail"
              count={resolvedResearch.length + data.deadEnds.length}
              expanded={showDeadEnds}
              onToggle={() => setShowDeadEnds(!showDeadEnds)}
            >
              <div className="audit-trail">
                {data.deadEnds.map(d => (
                  <div key={d.id} className="audit-row dead-end">
                    <span className="audit-tag">RULED OUT</span>
                    <span><strong>{d.ruledOut}</strong>{d.reason && <> · {d.reason}</>}</span>
                  </div>
                ))}
                {resolvedResearch.map(r => (
                  <div key={r.id} className="audit-row resolved">
                    <span className="audit-tag green">RESOLVED</span>
                    <span><strong>{r.question}</strong>{r.resolvedNote && <> · {r.resolvedNote}</>}</span>
                  </div>
                ))}
              </div>
            </SecondaryBlock>
          )}
        </div>
      )}

      {/* Empty state when nothing's started yet */}
      {data.chain.length === 0 && !data.math && !theory?.thesis && (
        <div className="empty-state" style={{ paddingTop: 60 }}>
          <strong>No title work yet</strong>
          The title agent writes the chain, worksheet, and theory when research runs; this legacy view only renders them.
        </div>
      )}
    </>
  );
}

// ─── Thesis panel ──────────────────────────────────────────
function ThesisPanel({
  theory, investigating, showSupporting, setShowSupporting,
}: {
  theory?: WorkingTheory;
  investigating: boolean;
  showSupporting: boolean;
  setShowSupporting: (b: boolean) => void;
}) {
  if (!theory && !investigating) return null;

  if (!theory && investigating) {
    return (
      <div className="thesis-panel">
        <div className="thesis-head">
          <span className="thesis-label">Title summary</span>
          <span className="thesis-conf">thinking…</span>
        </div>
        <div style={{ padding: 18, color: 'var(--text-faint)', fontSize: 13, fontStyle: 'italic' }}>
          Reading the deal evidence and building a working theory…
        </div>
      </div>
    );
  }

  if (!theory) return null;

  const hasThesis = !!theory.thesis;
  const hasFields = Object.keys(theory.fields || {}).length > 0;
  const conf = theory.confidence ?? 50;
  const confColor =
    conf >= 75 ? 'var(--green)' :
    conf >= 50 ? 'var(--accent)' :
    'var(--amber)';

  return (
    <div className="thesis-panel">
      <div className="thesis-head">
        <div className="thesis-head-left">
          <span className="thesis-label">Title summary</span>
          {theory.headlineFigure && (
            <span className="thesis-headline">{theory.headlineFigure}</span>
          )}
        </div>
        <div className="thesis-head-right">
          <span className="thesis-conf" style={{ color: confColor }}>
            <strong>{conf}%</strong> confident
          </span>
          {theory.updatedAt && (
            <span className="thesis-updated">{formatTime(theory.updatedAt)}</span>
          )}
        </div>
      </div>

      {hasThesis && (
        <div
          className="thesis-body"
          dangerouslySetInnerHTML={{ __html: renderInlineMd(theory.thesis!) }}
        />
      )}

      {!hasThesis && hasFields && (
        // Backwards-compat fallback for theories from before the thesis migration
        <div className="thesis-body">
          {Object.entries(theory.fields).map(([k, v]) => (
            <div key={k} style={{ marginBottom: 6 }}>
              <strong>{humanizeKey(k)}:</strong>{' '}
              <span dangerouslySetInnerHTML={{ __html: renderInlineMd(v) }} />
            </div>
          ))}
        </div>
      )}

      {theory.criticalGap && (
        <div className="thesis-gap">
          <span className="thesis-gap-tag">CRITICAL GAP</span>
          <span>{theory.criticalGap}</span>
        </div>
      )}

      {hasFields && hasThesis && (
        <div className="thesis-supporting-toggle">
          <button onClick={() => setShowSupporting(!showSupporting)}>
            {showSupporting ? '▾' : '▸'} Supporting detail ({Object.keys(theory.fields).length})
          </button>
          {showSupporting && (
            <div className="thesis-supporting">
              {Object.entries(theory.fields).map(([k, v]) => (
                <div key={k} className="thesis-supporting-row">
                  <div className="thesis-supporting-label">{humanizeKey(k)}</div>
                  <div
                    className="thesis-supporting-value"
                    dangerouslySetInnerHTML={{ __html: renderInlineMd(v) }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reusable secondary section (collapsible) ───────────────
function SecondaryBlock({
  title, count, expanded, onToggle, children,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="secondary-block">
      <button className="secondary-block-head" onClick={onToggle}>
        <span className="secondary-block-chev">{expanded ? '▾' : '▸'}</span>
        <span className="secondary-block-title">{title}</span>
        <span className="secondary-block-count">{count}</span>
      </button>
      {expanded && <div className="secondary-block-body">{children}</div>}
    </div>
  );
}

function ResearchItem({ item }: { item: OutstandingResearch }) {
  const approachLabel: Record<string, string> = {
    'pull-doc': 'PULL', 'search': 'SEARCH', 'ask-client': 'ASK',
  };

  return (
    <div className={`research-item ${item.priority}`}>
      <div className="research-priority">{item.priority.toUpperCase()}</div>
      <div className="research-body">
        <div className="research-question">{item.question}</div>
        <div className="research-target">
          <span className={`research-approach ${item.approach}`}>{approachLabel[item.approach] || 'TODO'}</span>
          {' '}{item.target}
          {item.sourceHint && (
            <a className="research-link" href={maybeUrl(item.sourceHint) || '#'}
              target={maybeUrl(item.sourceHint) ? '_blank' : undefined}
              rel="noreferrer"
              onClick={e => { if (!maybeUrl(item.sourceHint!)) e.preventDefault(); }}
            >
              ↗ {maybeUrl(item.sourceHint) ? 'open' : item.sourceHint}
            </a>
          )}
        </div>
        {item.reason && <div className="research-reason">— {item.reason}</div>}
      </div>
    </div>
  );
}

function StageRow({ stage }: { stage: InvestigationStage }) {
  return (
    <div className="stage-row">
      <div className="stage-row-head">
        <span className="stage-num">#{stage.ordinal}</span>
        <span className="stage-goal">{stage.goal}</span>
        <span className="stage-time">{formatTime(stage.createdAt)}</span>
      </div>
      <div className="stage-method">Method: {stage.method}</div>
      <div className="stage-conclusion"
        dangerouslySetInnerHTML={{ __html: renderInlineMd(stage.conclusion) }} />
      {stage.citedSources && stage.citedSources.length > 0 && (
        <div className="stage-sources">
          {stage.citedSources.map((s, i) => {
            const isFileRef = s.kind === 'document' && s.ref && s.ref.startsWith('f_');
            if (isFileRef) {
              return (
                <button
                  key={i}
                  className="stage-source clickable"
                  onClick={() => window.open(filesApi.downloadUrl(s.ref), '_blank')}
                  title="Open source document"
                >{s.label} ↗</button>
              );
            }
            return <span key={i} className="stage-source">{s.label}</span>;
          })}
        </div>
      )}
    </div>
  );
}


function humanizeKey(k: string): string {
  return k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
}

function maybeUrl(s: string): string | null {
  if (/^https?:\/\//.test(s)) return s;
  return null;
}

function clerkUrl(county: string, _trs: string): string {
  const c = county.toLowerCase();
  if (c.includes('weld'))     return 'https://recording.weld.gov/web/';
  if (c.includes('converse')) return 'https://www.conversecountywy.gov/clerk/';
  if (c.includes('campbell')) return 'https://www.ccgov.net/271/Clerk';
  return `https://www.google.com/search?q=${encodeURIComponent(county + ' clerk recorder records search')}`;
}
